import React, { useState, useEffect } from "react";
import {
    Table,
    Card,
    Placeholder,
    Badge,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useKpi } from "@features/admin/kpi/hooks/useKpi";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { Kpi } from "@features/admin/kpi/types/kpi";
import UpdateKpiModal from "./UpdateKpiModal";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import { useKpiAction } from "@features/admin/kpi/hooks/useKpiAction";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import NoResult from "@core/components/NoResult";
import { useDebounce } from "@core/hooks/useDebounce";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { hasActiveListCriteria } from "@core/utils/listQuery";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import { ENTRY_MODE_CALCULATED, formatEntryModeLabel } from "../types/kpiEntryMode";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import {
    NAME_SORT_FILTER_FIELD,
    SORT_BY_KPI_NAME,
    SORT_DIR_ASC,
    countContentFilters,
    contentFilterKeys,
    filtersWithoutSort,
    normalizeSortDir,
    readSortDirFromSearchParams,
    withCatalogSort,
} from "@core/constants/listSort";

const KPI_STATUS_ACTION_CODES = [ACTION_CODES.ENABLE_KPI, ACTION_CODES.DISABLE_KPI];

const KPI_FILTER_FIELDS: FilterField[] = [
    NAME_SORT_FILTER_FIELD,
    { name: 'kpiName',      label: 'KPI Name',    type: 'text', placeholder: 'Filter by KPI name' },
    { name: 'activityName', label: 'Activity',     type: 'text', placeholder: 'Filter by activity name' },
    { name: 'description',  label: 'Description',  type: 'text', placeholder: 'Filter by description' },
    { name: 'dataTypeName', label: 'Data Type',    type: 'text', placeholder: 'Filter by data type' },
    { name: 'unitName',     label: 'Unit',         type: 'text', placeholder: 'Filter by unit name' },
    { name: 'entryMode',    label: 'Entry mode',   type: 'select', options: [{ value: 'MANUAL', label: 'Manual' }, { value: 'CALCULATED', label: 'Calculated' }] },
    { name: 'isActive',     label: 'Status',       type: 'select', options: [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }] },
];

const KpiTable: React.FC<{ showPagination?: boolean; refreshSignal?: number }> = ({ showPagination = false, refreshSignal }) => {
    const [searchParams] = useSearchParams();
    const resolvedNationId = useScopeId() ?? 0;

    const [page, setPage] = useState(() => Number(searchParams.get("page") || "1"));
    const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
    const debouncedSearch = useDebounce(search, 300);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const result: Record<string, any> = {};
        searchParams.forEach((value, key) => {
            if (key !== "q" && key !== "page") result[key] = value;
        });
        result.sortDir = readSortDirFromSearchParams(searchParams);
        return result;
    });
    const [selectedKpi, setSelectedKpi] = useState<Kpi | null>(null);
    const [showEditKpiModal, setShowEditKpiModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<{ kpi: Kpi; kind: StatusChangeKind } | null>(null);
    const hasSearchOrFilters =
        !!debouncedSearch ||
        hasActiveListCriteria(activeFilters, contentFilterKeys(Object.keys(activeFilters)));

    const showError = useApiError();
    const { busy: statusBusy, doKpiDisable, doKpiEnable } = useKpiAction();

    // ADMIN-only enable/disable gated on NATION-scoped action codes.
    const { hasPermission } = usePermissions({
        scopeType: 'NATION',
        scopeId: resolvedNationId || undefined,
        actionCodes: KPI_STATUS_ACTION_CODES,
    });
    const canDisable = hasPermission(ACTION_CODES.DISABLE_KPI);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_KPI);

    useSearchUrlSync({ query: debouncedSearch, filters: activeFilters, page });

    // Fetch KPIs
    const { current_page, data, from, last_page, per_page, total, loading, reload } = useKpi(
        withCatalogSort(
            {
                nationId: resolvedNationId,
                page,
                ...(hasSearchOrFilters ? {} : { all: true }),
                ...(debouncedSearch ? { freetext: debouncedSearch } : {}),
                ...filtersWithoutSort(activeFilters),
                sortDir: normalizeSortDir(activeFilters.sortDir),
            },
            SORT_BY_KPI_NAME
        )
    );

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        from,
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });

    // open the modal only after selectedKpi has actually updated
    useEffect(() => {
        // Only open if selectedKpi is set
        if (selectedKpi) {

            // Open the modal
            setShowEditKpiModal(true);
        }
    }, [selectedKpi]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    // Reload when a sibling create/edit succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    const handleStatusConfirm = async () => {
        if (!statusConfirm || !resolvedNationId) return;
        const { kpi, kind } = statusConfirm;
        const response = kind === "disable"
            ? await doKpiDisable(resolvedNationId, Number(kpi.kpi_id))
            : await doKpiEnable(resolvedNationId, Number(kpi.kpi_id));

        if (response.isSuccess()) {
            showSuccess(response.getData()?.message ?? `KPI ${kind}d successfully.`);
            setStatusConfirm(null);
            void reload();
            return;
        }

        showError(response.getError() as ErrorResponse);
    };

    // Shared by the desktop table row and the mobile card so the action set
    // (and its permission gating) stays defined in one place.
    const renderKpiActions = (r: (typeof data)[number]) => {
        const id = Number(r.kpi_id);
        return (
            <TableActions aria-label={`Actions for KPI ${r.kpi_name}`}>
                <TableActionButton
                    icon={<FiEdit2 aria-hidden="true" />}
                    label="Edit"
                    primary
                    onClick={() => { setSelectedKpi(r); setShowEditKpiModal(true); }}
                    data-testid={`kpi-edit-${id}`}
                />
                {r.is_active && canDisable && (
                    <TableActionButton
                        icon={<FiSlash />}
                        label="Disable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--danger"
                        onClick={() => setStatusConfirm({ kpi: r, kind: "disable" })}
                        data-testid={`kpi-disable-${id}`}
                    />
                )}
                {!r.is_active && canEnable && (
                    <TableActionButton
                        icon={<FiCheckCircle />}
                        label="Enable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--success"
                        onClick={() => setStatusConfirm({ kpi: r, kind: "enable" })}
                        data-testid={`kpi-enable-${id}`}
                    />
                )}
            </TableActions>
        );
    };

    return (
        <div data-testid="kpi-table">
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search KPIs…"
                    filterFields={KPI_FILTER_FIELDS}
                    initialQuery={search}
                    initialFilters={activeFilters}
                    onQueryChange={(q) => { setSearch(q); setPage(1); }}
                    onFiltersApply={(f) => {
                        setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
                        setPage(1);
                    }}
                    onClear={() => {
                        setSearch("");
                        setActiveFilters({ sortDir: SORT_DIR_ASC });
                        setPage(1);
                    }}
                    loading={loading}
                    activeFilterCount={countContentFilters(activeFilters)}
                    testId="kpi-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="kpi-table-card">
                <Card.Body className="p-0">
                        <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table" data-testid="kpi-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th>#</th>
                                    <th>KPI Name</th>
                                    <th>Description</th>
                                    <th>Entry mode</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 140 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider" style={{ opacity: loading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}>
                                {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`} data-testid={`skeleton-row-${i}`}>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={5} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr data-testid="no-kpis-row">
                                        <td colSpan={6} className="py-5">
                                            <NoResult line_1="No KPIs found." line_2="Try adjusting filters or create a new KPI." />
                                        </td>
                                    </tr>
                                )}
                                {data.length > 0 && data.map((r: typeof data[number], i: number) => {
                                    const id = Number(r.kpi_id);
                                    const rowNumber = (from != null) ? from + i : i + 1;
                                    const entryMode = (r.entry_mode || "MANUAL").toUpperCase();
                                    return (
                                        <tr key={id} data-testid={`kpi-row-${id}`} className="bg-white">
                                            <td>
                                                {rowNumber}
                                            </td>
                                            <td className="fw-semibold">{r.kpi_name}</td>
                                            <td >{r.description}</td>
                                            <td>
                                                <Badge
                                                    {...subtleBadgeProps(
                                                        entryMode === ENTRY_MODE_CALCULATED ? "info" : "secondary"
                                                    )}
                                                    pill
                                                >
                                                    {formatEntryModeLabel(entryMode)}
                                                </Badge>
                                            </td>
                                            <td><StatusPill active={!!r.is_active} /></td>
                                            <td className="text-end">
                                                {renderKpiActions(r)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        </div>

                        {/* Mobile (< md): stacked cards */}
                        <div className="ntcog-card-list d-md-none" data-testid="kpi-cards">
                            {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                    <EntityCard
                                        testId={`kpi-card-skeleton-${i}`}
                                        title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                        description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                    />
                                </div>
                            ))}
                            {!loading && data.length === 0 && (
                                <NoResult line_1="No KPIs found." line_2="Try adjusting filters or create a new KPI." />
                            )}
                            {data.length > 0 && data.map((r: typeof data[number]) => {
                                const id = Number(r.kpi_id);
                                return (
                                    <div key={id} className="ntcog-card-list__item">
                                        <EntityCard
                                            testId={`kpi-card-${id}`}
                                            ariaLabel={`KPI ${r.kpi_name}`}
                                            title={<span className="fw-semibold">{r.kpi_name}</span>}
                                            status={
                                                <div className="d-flex flex-wrap gap-1">
                                                    <Badge
                                                        {...subtleBadgeProps(
                                                            (r.entry_mode || "MANUAL").toUpperCase() === ENTRY_MODE_CALCULATED
                                                                ? "info"
                                                                : "secondary"
                                                        )}
                                                        pill
                                                    >
                                                        {formatEntryModeLabel(r.entry_mode)}
                                                    </Badge>
                                                    <StatusPill active={!!r.is_active} />
                                                </div>
                                            }
                                            actions={renderKpiActions(r)}
                                            description={r.description || undefined}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} KPIs`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateKpiModal
                show={showEditKpiModal}
                onHide={() => setShowEditKpiModal(false)}
                kpi={selectedKpi}
                onChanged={reload}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="KPI"
                entityName={statusConfirm?.kpi.kpi_name ?? ""}
                busy={statusBusy}
                onConfirm={() => { void handleStatusConfirm(); }}
                onCancel={() => setStatusConfirm(null)}
                bodyText={statusConfirm?.kind === "disable" ? (
                    <>
                        Are you sure you want to disable <strong>{statusConfirm?.kpi.kpi_name}</strong>? It can no longer be assigned to activities. Existing report data is unaffected.
                    </>
                ) : undefined}
            />
        </div>
    );
};

export default KpiTable;